<template>
	<v-dialog v-model="showGetFromGroupwareDialog" width="77rem" persistent>
		<add-data-from-groupware :picked-files="pickedTiffItems" @close="closeGetFromGroupwareDialog" @accept="onAccept"
			@remove="removePickedTiff" @proceed="proceedToDtmOrthoAssignment" />
	</v-dialog>
	<!-- tiff の組だけが通る子。facade はこの子の setup に閉じ、2 件揃ったときだけマウントする（GI-17） -->
	<dtm-ortho-type-flow v-if="dtmOrthoAssigning" :pair="pickedTiffPair" @previous="backToGroupwarePicker"
		@cancel="closeGetFromGroupwareDialog" @finished="closeGetFromGroupwareDialog" />
	<v-dialog v-model="showFileSelectionDialog" width="600px" persistent>
		<file-selection-dialog :allowedFileTypes="allowedFileTypes" :source="uploadSource" :preselectedFile="fileForDialog"
			@close="closeFileSelectionDialog" @confirm="onFileSelected"
			@requestGroupwareReselection="onRequestGroupwareReselection" />
	</v-dialog>
	<v-dialog v-model="showTerrainDataDialog" width="600px" persistent>
		<add-terrain-data ref="add_terrain_data" @upload="uploadTerrainData" @previous="backToFileSelection"
			@close="closeTerrainData" :allowedFileTypes="allowedFileTypes" :axis_show="axisShow"
			:is_show="showTerrainDataDialog" :default_file_name="this.selectedFiles[0].name"
			:is_drawing_2d_image="type === 'drawing2dImage'" :resolvedType="type" />
	</v-dialog>
</template>

<script>
import DtmOrthoTypeFlow from "@/components/AddData/DtmOrthoTypeFlow.vue";
import { toStorageNodeRef } from "@/components/AddData/dtm-ortho/dtmOrtho.types";
import { FILE_SELECTION_ERROR_KEYS } from "@/components/AddData/fileSelectionResolver";
import {
	GROUPWARE_TIFF_PAIR_SIZE,
	groupwareItemKey,
	resolveGroupwareAccept,
} from "@/components/AddData/groupwareSelectionResolver";
import { useUploadTracker } from "@/composables/useUploadTracker";
import { importDesign } from "@/utils/designApiUtils";
import {
	handleSingleUploadError,
	handleSingleUploadSuccess,
	notifyError,
} from "@/utils/notificationUtils";
import { importPointCloud } from "@/utils/pointCloudApiUtils";
import axios from "axios";
import { mapMutations } from "vuex";
import FileSelectionDialog from "../AddData/FileSelectionDialog.vue";
import addDataFromGroupware from "../AddData/addDataFromGroupware.vue";
import addTerrainData from "../AddData/addTerrainData.vue";
import { getAllowedFileTypesString, uploadSources } from "./uploadTypeResolver";

const STATES = {
	SELECT_FROM_GROUPWARE: "SELECT_FROM_GROUPWARE",
	FILE_SELECTION: "FILE_SELECTION",
	ADD_TERRAIN_DATA: "ADD_TERRAIN_DATA",
	CLOSE_ALL: "CLOSE_ALL",
};

class State {
	constructor() {
		this.stateList = Object.values(STATES);
		this.currentStateIndex = 0;
	}

	getCurrentState() {
		return this.stateList[this.currentStateIndex];
	}

	next() {
		if (this.currentStateIndex < this.stateList.length - 1) {
			this.currentStateIndex++;
			return;
		}
		console.error("Out of state");
	}
}

export default {
	name: "addDataFromGroupwareSequence",
	components: {
		addDataFromGroupware,
		FileSelectionDialog,
		addTerrainData,
		DtmOrthoTypeFlow,
	},
	data() {
		return {
			state: undefined,
			selectedFiles: [],
			fileForDialog: undefined,
			type: "",
			axisShow: "",
			allowedFileTypes: "",
			uploadSource: uploadSources.groupware,
			/** フォルダ・ページをまたいで積み上げた tiff（0〜2 件。GI-16） */
			pickedTiffItems: [],
			/** tiff の組の種別選択を重ねて表示中か */
			dtmOrthoAssigning: false,
		};
	},
	created() {
		this.state = new State();
	},
	mounted() {
		this.stateMachine = new State();
	},
	unmounted() {
		this.stateMachine = new State();
	},
	computed: {
		showGetFromGroupwareDialog() {
			return (
				this.state.getCurrentState() === STATES.SELECT_FROM_GROUPWARE &&
				!this.dtmOrthoAssigning
			);
		},
		showFileSelectionDialog() {
			return this.state.getCurrentState() === STATES.FILE_SELECTION;
		},
		showTerrainDataDialog() {
			return this.state.getCurrentState() === STATES.ADD_TERRAIN_DATA;
		},
		/** 積んだ 2 件を種別選択に渡す組にする（選択順のまま。要素ごとの bucketId を保つ。GI-36） */
		pickedTiffPair() {
			return {
				origin: "storage",
				files: this.pickedTiffItems.map((item) =>
					toStorageNodeRef(item.bucketId, item),
				),
			};
		},
	},
	methods: {
		...mapMutations(["set_snackbar"]),
		closeGetFromGroupwareDialog() {
			this.$emit("close");
		},
		onAccept(detail) {
			// tiff の組は積み上げの経路へ。tiff を含まない選択は既存の本体をそのまま実行する（GI-17）
			const resolution = resolveGroupwareAccept(detail, this.pickedTiffItems);
			if (resolution.kind === "accumulate") {
				this.pickedTiffItems = [...resolution.picked];
				return;
			}
			if (resolution.kind === "tiffPair") {
				this.pickedTiffItems = [...resolution.picked];
				this.dtmOrthoAssigning = true;
				return;
			}
			if (resolution.kind === "invalid") {
				notifyError(
					this,
					this.$t(FILE_SELECTION_ERROR_KEYS[resolution.reason]),
				);
				return;
			}

			this.selectedFiles = detail;
			this.fileForDialog = this.selectedFiles[0];
			this.allowedFileTypes = getAllowedFileTypesString(
				uploadSources.groupware,
			);
			this.state.next();
		},
		removePickedTiff(key) {
			this.pickedTiffItems = this.pickedTiffItems.filter(
				(item) => groupwareItemKey(item) !== key,
			);
		},
		proceedToDtmOrthoAssignment() {
			if (this.pickedTiffItems.length !== GROUPWARE_TIFF_PAIR_SIZE) return;
			this.dtmOrthoAssigning = true;
		},
		backToGroupwarePicker() {
			// 積んだ 2 件は保持し、ピッカーと選択済みリストに戻る（GI-30）
			this.dtmOrthoAssigning = false;
		},
		closeFileSelectionDialog() {
			this.$emit("close");
		},
		onRequestGroupwareReselection() {
			this.fileForDialog = undefined;
			this.state.currentStateIndex = 0;
		},
		onFileSelected(file, resolvedType) {
			this.fileForDialog = file;
			this.type = resolvedType;
			this.axisShow = resolvedType === "pointcloud";
			this.state.next();
		},
		backToFileSelection() {
			this.state.currentStateIndex = 1;
		},
		closeTerrainData() {
			this.$emit("close");
		},
		async uploadTerrainData(dialogData) {
			if (!dialogData) {
				this.closeTerrainData();
				return;
			}

			const name = dialogData[1];
			const bucket_id = this.selectedFiles[0].bucketId;
			const node_id = this.selectedFiles[0].id;
			const date = dialogData[2];
			const time = `${dialogData[3]}:00`;
			const axis = this.axisShow ? dialogData[4] : undefined;
			const distance_unit = this.axisShow ? dialogData[5] : undefined;
			const selectedType = dialogData[dialogData.length - 1] || this.type;

			try {
				this.$store.commit("setLoading", true);
				if (selectedType === "pointcloud") {
					const { point_cloud_id } = await importPointCloud(
						this.$route.query.siteId,
						{
							bucket_id,
							node_id,
							name,
							date,
							time,
							file_name: this.selectedFiles[0].name,
							axis,
							distance_unit,
						},
					);
					useUploadTracker().trackItem(
						{ dataType: "pointcloud", id: point_cloud_id },
						this.$route.query.siteId,
					);
					this.$store.dispatch(
						"get_point_cloud_list",
						this.$route.query.siteId,
					);
				} else if (selectedType === "design") {
					const { design_id } = await importDesign(this.$route.query.siteId, {
						bucket_id,
						node_id,
						name,
						date,
						time,
					});
					useUploadTracker().trackItem(
						{ dataType: "design", id: design_id },
						this.$route.query.siteId,
					);
					this.$store.dispatch("get_design_list", this.$route.query.siteId);
				} else if (selectedType === "lineworks") {
					const url = `${import.meta.env.VITE_API_BASE}/assetLink/lineworks/${this.$route.query.siteId}`;
					const res = await axios.post(url, {
						bucket_id,
						node_id,
						name,
						// Fallback unit for DXF without $INSUNITS
						site_distance_unit: this.$store.state.site?.units?.distance,
					});
					useUploadTracker().trackItem(
						{ dataType: "lineworks", id: res.data.lineworks_id },
						this.$route.query.siteId,
					);
					this.$store.dispatch("get_design_list", this.$route.query.siteId);
				} else {
					console.warn(`Unsupported type: ${selectedType}`);
				}

				// Show success message (lineworks is synchronous, others are async)
				if (selectedType === "lineworks") {
					handleSingleUploadSuccess(this, name, {
						customMessage: `${name} ${this.$t("UPLOAD")} ${this.$t("successful")}`,
					});
				} else {
					handleSingleUploadSuccess(this, name);
				}
			} catch (error) {
				handleSingleUploadError(this, error, name);
			} finally {
				this.$store.commit("setLoading", false);
			}

			this.$emit("close");
		},
	},
};
</script>
